import { FC, Fragment, memo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ScrapersDto, useGetCampaignQuery, CampaignFiltersDto, GetCampaignFiltersApiResponse } from '@api/apiCompat';
import { SOURCE_INFO } from '@constants/formFields';
import { useCampaignFiltersWithFallback } from '@hooks/api/useCampaignFiltersWithFallback';
import { useCampaignSourcesWithFallback } from '@hooks/api/useCampaignSourcesWithFallback';
import { prefillMatchedSources } from '@pages/LeadScraper/components/ScraperSettings/components/ScraperFlow/utils/prefill';
import { useLeadScraperContext } from '@pages/LeadScraper/context';

import { Box, Button, CircularProgress } from '@mui/material';

import ScraperSettingsAutocomplete from './ScraperSettingsAutocomplete';
import SelectedSourceCard from './SelectedSourceCard';

// Helper functions for filter normalization
function isCampaignFiltersDto(filter: any): filter is CampaignFiltersDto {
  return 'campaign_filters_id' in filter && 'key' in filter && 'type' in filter;
}

function normalizeCampaignFilters(
  filters: GetCampaignFiltersApiResponse | Array<{
    filter_id: number;
    filter_name: string;
    filter_type: string;
    required: boolean;
    placeholder: string;
  }>
): CampaignFiltersDto[] {
  if (!filters || !Array.isArray(filters)) {
    return [];
  }
  
  return filters.map((filter: any) => {
    // If it's already in the correct format, return it
    if (isCampaignFiltersDto(filter)) {
      return filter;
    }
    
    // Otherwise, transform the fallback format to CampaignFiltersDto
    return {
      campaign_filters_id: filter.filter_id,
      key: filter.filter_name,
      type: filter.filter_type || 'text',
      campaign_type_id_campaign_type: filter.campaign_type_id || 0, // or appropriate default
      price: 0 // or appropriate default
    } as CampaignFiltersDto;
  });
}

const SourceSettingsForm: FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { control, formState, unregister, trigger, setValue } = useFormContext();
  const { data: rawSourcesData, isLoading: isLoadingSources } = useCampaignSourcesWithFallback(id);
  // Ensure sourcesData is always an array
  const sourcesData: ScrapersDto[] = Array.isArray(rawSourcesData) ? rawSourcesData : [];

  const autocompleteFields: ScrapersDto[] = useWatch({ name: SOURCE_INFO.AUTOCOMPLETE, control }) || [];

  const { data: rawFiltersData, isLoading: isFiltersLoading } = useCampaignFiltersWithFallback(id);
  const filtersData = normalizeCampaignFilters(rawFiltersData);

  const { isEdit } = useLeadScraperContext();
  const { campaignId } = useParams();

  const { data: campaignData } = useGetCampaignQuery(
    { id: campaignId as string },
    { refetchOnMountOrArgChange: true, skip: !campaignId },
  );

  const handleRemove = (idx: number) => {
    unregister(`${SOURCE_INFO.AUTOCOMPLETE}.${idx}`);
    trigger(SOURCE_INFO.AUTOCOMPLETE);
  };

  useEffect(() => {
    if (isEdit && campaignId && campaignData && sourcesData && filtersData) {
      const matchedSources = prefillMatchedSources({ sourcesData, campaignData, filtersData });
      setValue(SOURCE_INFO.AUTOCOMPLETE, matchedSources);
      trigger();
    }
  }, [campaignData, setValue]);

  return (
    <Box display="flex" flexDirection="column" width="100%" pt={2} pb={1} gap={2}>
      <ScraperSettingsAutocomplete
        options={sourcesData}
        isLoading={isLoadingSources}
        autocompleteFields={autocompleteFields}
      />

      {isFiltersLoading ? (
        <Box width="100%" height="50px" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : null}

      {filtersData &&
        !isFiltersLoading &&
        autocompleteFields?.map((field, index) => (
          <Fragment key={field.scrapers_id ?? index}>
            <SelectedSourceCard
              name={field.scraper_name || ''}
              price={field.price ?? 0}
              onRemove={handleRemove}
              index={index}
              control={control}
              renderData={filtersData}
              scraperId={field.scrapers_id ?? 0}
            />
          </Fragment>
        ))}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        type="submit"
        disabled={!formState.isValid || isFiltersLoading}
      >
        {t('dashboard.newScraper.nextStep')}
      </Button>
    </Box>
  );
};

export default memo(SourceSettingsForm);
